import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { useState, useEffect, useRef } from "react";
import { useIconPack } from "../../context/IconPackContext";
import styles from "./Sidebar.module.css";
import { useLanguage } from "../../context/LanguageContext";
import { useMenu } from "../../context/MenuContext";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { getIcon } = useIconPack();
  const { translateByModule, translateLabel } = useLanguage();
  const { menuSettings, isLoading } = useMenu();

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const fileIcon = localStorage.getItem("fileIcon") || "file";
  const sidebarIcon = localStorage.getItem("sidebarIcon") || "folder";

  useEffect(() => {
    if (isOpen) {
      setIsSearchOpen(false);
      setSearchQuery("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  const tableModules = menuSettings.modules || [];
  const reportModules = menuSettings.reports || [];
  const dashboardModules = menuSettings.dashboard || [];
  const canAccessDeliveryOperations = dashboardModules.some(
    (module) => module.key === "delivery-operations",
  );
  const payductorDevEnabled = process.env.REACT_APP_PAYDUCTOR_ENABLED === "true" ||
    window.location.hostname === "admin-dev.smoke.team" ||
    window.location.hostname === "localhost";

  const processTemplateModule = tableModules.find(
    (module) => module.table_name === "transaction_template",
  );

  const tModule = (moduleName: string) => {
    return translateByModule(
      moduleName.toLowerCase(),
      moduleName.toLowerCase(),
    );
  };

  const filteredTableModules = tableModules.filter((module) => {
    const name = tModule(module.name).toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  const handleOpenSearch = () => {
    setIsSearchOpen(true);
    setSearchQuery("");
  };

  const handleCloseSearch = () => {
    setIsSearchOpen(false);
    setSearchQuery("");
  };

  const handleLinkClick = () => {
    handleCloseSearch();
    onClose();
  };

  return (
    <>
      {isOpen && <div className={styles.overlay} onClick={onClose} />}

      <aside
        className={`${styles.sidebar} ${isOpen ? styles.open : styles.closed}`}
      >
        <div className={styles.header}>
          <span className={styles.title}>{translateLabel("sidebar")}</span>

          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon
              icon={getIcon("xmark")}
              className={styles.closeIcon}
            />
          </button>
        </div>

        <nav className={styles.nav}>
          {isLoading ? (
            <div className={styles.menuLoading}>
              <div className={styles.spinner} />
              <span>{translateLabel("loading")}</span>
            </div>
          ) : (
            <>
              {!isSearchOpen && (
                <>
                  <div className={styles.section}>
                    <NavLink to="/node-monitor" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ""}`} onClick={onClose}>
                      <FontAwesomeIcon icon={getIcon("server")} className={styles.icon} /><span>Мониторинг нод</span>
                    </NavLink>
                    {dashboardModules.map((module) => (
                      <NavLink
                        key={`dashboard_${module.id}`}
                        to={`/${module.key}`}
                        className={({ isActive }) =>
                          `${styles.navLink} ${isActive ? styles.active : ""}`
                        }
                        onClick={onClose}
                      >
                        <FontAwesomeIcon
                          icon={getIcon(sidebarIcon)}
                          className={styles.icon}
                        />
                        <span>{tModule(module.name)}</span>
                      </NavLink>
                    ))}

                    {canAccessDeliveryOperations && (
                      <>
                        <NavLink
                          to="/delivery-export-columns"
                          className={({ isActive }) =>
                            `${styles.navLink} ${isActive ? styles.active : ""}`
                          }
                          onClick={onClose}
                        >
                          <FontAwesomeIcon
                            icon={getIcon("table")}
                            className={styles.icon}
                          />
                          <span>{translateLabel("deliveryExcelColumns")}</span>
                        </NavLink>
                        <NavLink
                          to="/app-downloads-settings"
                          className={({ isActive }) =>
                            `${styles.navLink} ${isActive ? styles.active : ""}`
                          }
                          onClick={onClose}
                        >
                          <FontAwesomeIcon
                            icon={getIcon("link")}
                            className={styles.icon}
                          />
                          <span>Web version address</span>
                        </NavLink>
                      </>
                    )}

                    {payductorDevEnabled && (canAccessDeliveryOperations || dashboardModules.some((module) => module.key === "axiome-balance")) && (
                      <>
                        <NavLink to="/payductor/methods" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ""}`} onClick={onClose}><FontAwesomeIcon icon={getIcon("credit-card")} className={styles.icon} /><span>Payductor methods</span></NavLink>
                        <NavLink to="/payductor/credentials" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ""}`} onClick={onClose}><FontAwesomeIcon icon={getIcon("key")} className={styles.icon} /><span>Payductor API keys</span></NavLink>
                        <NavLink to="/payductor/checkout" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ""}`} onClick={onClose}><FontAwesomeIcon icon={getIcon("pen")} className={styles.icon} /><span>Checkout settings</span></NavLink>
                        <NavLink to="/payductor/rates" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ""}`} onClick={onClose}><FontAwesomeIcon icon={getIcon("chart-line")} className={styles.icon} /><span>Exchange rates</span></NavLink>
                        <NavLink to="/payductor/payments" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ""}`} onClick={onClose}><FontAwesomeIcon icon={getIcon("receipt")} className={styles.icon} /><span>Payductor payments</span></NavLink>
                      </>
                    )}

                    <NavLink
                      to="/transaction-template"
                      className={({ isActive }) =>
                        `${styles.navLink} ${isActive ? styles.active : ""}`
                      }
                      onClick={onClose}
                    >
                      <FontAwesomeIcon
                        icon={getIcon("exchange-alt")}
                        className={styles.icon}
                      />
                      <span>{translateLabel("transactions")}</span>
                    </NavLink>

                    <NavLink
                      to="/process-view"
                      className={({ isActive }) =>
                        `${styles.navLink} ${isActive ? styles.active : ""}`
                      }
                      onClick={onClose}
                    >
                      <FontAwesomeIcon
                        icon={getIcon("diagram-project")}
                        className={styles.icon}
                      />
                      <span>{translateLabel("process")}</span>
                    </NavLink>

                    <NavLink
                      to="/system-status"
                      className={({ isActive }) =>
                        `${styles.navLink} ${isActive ? styles.active : ""}`
                      }
                      onClick={onClose}
                    >
                      <FontAwesomeIcon
                        icon={getIcon("microchip")}
                        className={styles.icon}
                      />
                      <span>{translateLabel("system-status")}</span>
                    </NavLink>

                    <NavLink
                      to="/menu-editor"
                      className={({ isActive }) =>
                        `${styles.navLink} ${isActive ? styles.active : ""}`
                      }
                      onClick={onClose}
                    >
                      <FontAwesomeIcon
                        icon={getIcon("sliders")}
                        className={styles.icon}
                      />
                      <span>
                        {translateLabel("menuEditor") || "Menu Editor"}
                      </span>
                    </NavLink>
                  </div>

                  {processTemplateModule && (
                    <div className={styles.section}>
                      <NavLink
                        to="/process-template"
                        className={({ isActive }) =>
                          `${styles.navLink} ${isActive ? styles.active : ""}`
                        }
                        onClick={onClose}
                      >
                        <FontAwesomeIcon
                          icon={getIcon("cog")}
                          className={styles.icon}
                        />
                        <span>{tModule(processTemplateModule.name)}</span>
                      </NavLink>

                      <NavLink
                        to="/process/view"
                        className={({ isActive }) =>
                          `${styles.navLink} ${isActive ? styles.active : ""}`
                        }
                        onClick={onClose}
                      >
                        <FontAwesomeIcon
                          icon={getIcon("eye")}
                          className={styles.icon}
                        />
                        <span>View Processes</span>
                      </NavLink>
                    </div>
                  )}

                  {tableModules.length > 0 && (
                    <>
                      <div className={styles.divider} />

                      <div className={styles.section}>
                        <div
                          className={styles.sectionHeader}
                          onClick={handleOpenSearch}
                          style={{ cursor: "pointer" }}
                        >
                          <FontAwesomeIcon
                            icon={faSearch}
                            className={styles.chevron}
                            style={{ transform: "none" }}
                          />
                          <FontAwesomeIcon
                            icon={getIcon("table")}
                            className={styles.icon}
                          />
                          <span>{translateLabel("tables")}</span>
                        </div>
                      </div>
                    </>
                  )}

                  {tableModules
                    .filter((table) => !!table.favorite)
                    .sort((a, b) => (a.favorite ?? 0) - (b.favorite ?? 0))
                    .map((module) => (
                      <NavLink
                        key={`table_${module.id}`}
                        to={`/modules/${module.id}`}
                        className={({ isActive }) =>
                          `${styles.navLink} ${isActive ? styles.active : ""}`
                        }
                        onClick={handleLinkClick}
                      >
                        <FontAwesomeIcon
                          icon={getIcon(fileIcon)}
                          className={styles.icon}
                        />
                        <span>{tModule(module.name)}</span>
                      </NavLink>
                    ))}
                </>
              )}

              {isSearchOpen && (
                <div className={styles.searchContainer}>
                  <div className={styles.searchHeader}>
                    <button
                      className={styles.backButton}
                      onClick={handleCloseSearch}
                    >
                      <FontAwesomeIcon icon={getIcon("arrow-left")} />
                    </button>
                    <input
                      ref={searchInputRef}
                      type="text"
                      className={styles.searchInput}
                      placeholder={
                        translateLabel("search") || "Search tables..."
                      }
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>

                  <div className={styles.searchResults}>
                    {filteredTableModules.length === 0 ? (
                      <div className={styles.noResults}>
                        {translateLabel("noResults") || "No tables found"}
                      </div>
                    ) : (
                      filteredTableModules.map((module) => (
                        <NavLink
                          key={`table_${module.id}`}
                          to={`/modules/${module.id}`}
                          className={({ isActive }) =>
                            `${styles.navLink} ${isActive ? styles.active : ""}`
                          }
                          onClick={handleLinkClick}
                        >
                          <FontAwesomeIcon
                            icon={getIcon(fileIcon)}
                            className={styles.icon}
                          />
                          <span>{tModule(module.name)}</span>
                        </NavLink>
                      ))
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </nav>
      </aside>
    </>
  );
}
